define((require, exports, module) => {
    'use strict';

    const router = require('system/router');
    const fileSystemPath = require('../fs/fileSystemPath');
    const UploadReason = require('../../UploadReason');
    const OverQuotaFail = require('../../fails/OverQuotaFail');
    const UserFileSizeLimitFail = require('../../fails/UserFileSizeLimitFail');
    const StorageFileSizeLimitFail = require('../../fails/StorageFileSizeLimitFail');

    const GB = 1024 * 1024 * 1024;
    const TB = 1024 * GB;

    /**
     * @namespace
     */
    const s3fs = {
        /**
         * Bytes
         * @type {number}
         */
        MAX_FILE_SIZE: 32 * GB,

        /**
         * @returns {string}
         */
        getWorkingDirectory() {
            let result = '';
            const { activeRoute, activeRequest } = router;

            if (activeRoute && activeRoute.id === '#storage-bucket-objects') {
                const { bucket } = activeRoute.params;

                if (bucket) {
                    const { Prefix } = activeRequest.query;

                    if (Prefix) {
                        result = fileSystemPath.join(bucket, Prefix);
                    } else {
                        result = bucket;
                    }
                }
            }

            return result;
        },

        /**
         * @returns {Promise}
         * @resolves {number} – in bytes
         * @rejects {Error}
         */
        getUserFileSizeLimit() {
            return Promise.resolve(s3fs.MAX_FILE_SIZE);
        },

        /**
         * @param {number} size – in bytes
         * @returns {Promise}
         * @resolves {number} – size
         * @rejects {UploadReason|Error}
         */
        checkFileSizeLessThanMcsLimit(size) {
            let promise = Promise.resolve(s3fs.MAX_FILE_SIZE);

            promise = promise.then((limit) => {
                let promise;

                if (size > limit) {
                    const source = UploadReason.SOURCE_WEB_CLIENT;
                    const stack = new Error('StorageFileSizeLimitFail');
                    const reason = new StorageFileSizeLimitFail(stack, source);

                    promise = Promise.reject(reason);
                } else {
                    promise = Promise.resolve(size);
                }

                return promise;
            });

            return promise;
        },

        /**
         * @param {number} size – in bytes
         * @returns {Promise}
         * @resolves {number} – size
         * @rejects {UploadReason|Error}
         */
        checkFileSizeLessThanUserLimit(size) {
            let promise = s3fs.getUserFileSizeLimit();

            promise = promise.then((limit) => {
                let promise;

                if (size > limit) {
                    const source = UploadReason.SOURCE_WEB_CLIENT;
                    const stack = new Error('UserFileSizeLimitFail');
                    const reason = new UserFileSizeLimitFail(
                        stack,
                        source,
                        limit,
                    );

                    promise = Promise.reject(reason);
                } else {
                    promise = Promise.resolve(size);
                }

                return promise;
            });

            return promise;
        },

        /**
         * @param {number} size – in bytes
         * @returns {Promise}
         * @resolves {number} – size
         * @rejects {UploadReason|Error}
         */
        checkFileSizeLessThanLimit(size) {
            let promise = s3fs.checkFileSizeLessThanUserLimit(size);

            promise = promise.then(() => {
                return s3fs.checkFileSizeLessThanMcsLimit(size);
            });

            return promise;
        },

        /**
         * @param {number} size – in bytes
         * @returns {Promise}
         * @resolves {number} – free space in bytes
         * @rejects {UploadReason|Error}
         */
        checkEnoughSpace(size) {
            return new Promise((resolve, reject) => {
                const free = 10 * TB; // TODO

                if (free < size) {
                    const source = UploadReason.SOURCE_WEB_CLIENT;
                    const stack = new Error('OverQuotaFail');
                    const reason = new OverQuotaFail(stack, source);

                    reject(reason);
                } else {
                    resolve(free);
                }
            });
        },
    };

    module.exports = s3fs;
});
